import { useEffect, useState } from "react";

import * as Api from "@modules/api";
import {
  getBookmarked,
  getCompleted,
  getFavorite,
  getHighlighted,
  getPaginatedPlayLists,
  getStarted,
  useGetBookmarked,
  useGetCompleted,
  useGetFavorite,
  useGetHighlighted,
  useGetMyPlaylists,
  useGetStarted,
} from "@lib/stores/myLibrary";
import LibraryProgressList from "@components/layouts/content/LibraryProgressList";
import { translate } from "@modules/localizations/translate";
import { useAppNavigate } from "@utils/hooks/useAppNavigate";
import TranslatedText from "@components/items/display/TranslatedText";
import {
  LibraryItemDetails,
  LibraryItemDetailsModal,
} from "@components/layouts/content/LibraryItemDetailsModal";
import {
  LibraryProgressListSeeMoreModal,
  LibraryType,
} from "@components/layouts/content/LibraryProgressListSeeMoreModal";
import {
  useAppDispatch,
  useAppSelector,
  clearSelectedPlayerContent,
} from "@lib/stores";
import { ContentActions } from "@lib/stores/content/slices";
import { useAudioPlayer } from "@modules/content/AudioPlayerContext";
import { useSelectedPlayerContent } from "@modules/content/hooks/useSelectedPlayerContent";
import { db } from "@modules/local/db";
import { useAppLiveQuery } from "@modules/local/hooks";
import useCurrentPlan from "@utils/hooks/useCurrentPlan";
import CreatePlaylistDialog from "@components/layouts/b2c/my-corner/CreatePlaylistDialog";
import PlaylistsSection from "@components/layouts/content/PlaylistsSection";
import { detailsPageType } from "@utils/helpers/detailsPageType";
import { SelectedPlayerContentSource } from "@lib/stores/content/types";
import { useContentSelector } from "@modules/content/hooks/useContentSelector";
import { useLockedContents } from "@modules/content/hooks/useLockedContent";

interface MyLibraryPageProps {
  id: string | undefined;
}

const PAGE_SIZE = 10;

export const MyLibraryPage = ({ id }: MyLibraryPageProps) => {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();

  const [itemDetailsModal, setItemDetailsModal] = useState<
    {
      isOpen: boolean;
      type: "Bookmarked" | "Highlighted" | undefined;
    } & LibraryItemDetails
  >({
    isOpen: false,
    type: undefined,
    id: undefined,
    contentEntityTypeId: undefined,
    title: undefined,
    author: undefined,
    image: undefined,
    count: undefined,
    progress: undefined,
    contentEntityType: undefined,
    isLocked: undefined,
  });
  const [seeMoreModal, setSeeMoreModal] = useState<{
    isOpen: boolean;
    type: LibraryType | undefined;
  }>({ isOpen: false, type: undefined });

  const [isCreatePlaylistDialogOpen, setIsCreatePlaylistDialogOpen] =
    useState<boolean>(false);

  const userId = useAppSelector((state) => state.auth.user.id ?? "");
  const user = useAppSelector((state) => state.auth.user);

  const { plan } = useCurrentPlan();

  const { result: flow } = useAppLiveQuery(
    () => db.readerFlows.get(userId),
    [userId]
  );

  const {
    setSelectedPlayerContent,
    getSelectedPlayerContentEntityItemsById,
    onPlayingChapterSelect,
    onFirstPlayingChapterSelect,
  } = useSelectedPlayerContent();

  const { onSelectContent } = useContentSelector();

  const { togglePlayer, changeIsAutoStart, load, unload } = useAudioPlayer();

  const onAudioSelect = (audioUrl: string | undefined) => {
    unload();

    if (audioUrl) {
      setTimeout(() => {
        load(audioUrl);
      }, 100);
    }
  };

  const {
    data: startedData,
    meta: startedMeta,
    status: startedStatus,
  } = useGetStarted();
  const {
    data: completedData,
    meta: completedMeta,
    status: completedStatus,
  } = useGetCompleted();
  const {
    data: favoriteData,
    meta: favoriteMeta,
    status: favoriteStatus,
  } = useGetFavorite();
  const {
    data: bookmarkedData,
    meta: bookmarkedMeta,
    status: bookmarkedStatus,
  } = useGetBookmarked();
  // const {
  //   data: highlightedData,
  //   meta: highlightedMeta,
  //   status: highlightedStatus,
  // } = useGetHighlighted();
  const {
    data: playlistsData,
    meta: playlistsMeta,
    status: playlistsStatus,
  } = useGetMyPlaylists();

  const { isLockedContent } = useLockedContents([
    ...startedData,
    ...completedData,
    ...favoriteData,
    ...bookmarkedData,
  ]);
  const lockedContents = isLockedContent.result;
  const lockedContentsObj = lockedContents?.reduce(
    (prev, curr) => {
      prev[curr.id!] = curr;

      return prev;
    },
    {} as {
      [key: string]: {
        id: string | undefined;
        isLocked: boolean;
        lockType: Api.Server.LockType | undefined;
      };
    }
  );

  const onItemPress = async (item: LibraryItemDetails) => {
    const isLocked = lockedContents?.find((l) => l.id === item.id)?.isLocked;

    onSelectContent(
      item,
      {},
      {
        isLocked,
        source: SelectedPlayerContentSource.MyLibrary,
      }
    );
  };

  useEffect(() => {
    getStarted({
      PageNumber: 1,
      PageSize: PAGE_SIZE,
      SortBy: Api.Server.SortBy.MostRecent,
    });

    getCompleted({
      PageNumber: 1,
      PageSize: PAGE_SIZE,
      SortBy: Api.Server.SortBy.MostRecent,
    });

    getFavorite({
      PageNumber: 1,
      PageSize: PAGE_SIZE,
      SortBy: Api.Server.SortBy.MostRecent,
    });

    getBookmarked({
      PageNumber: 1,
      PageSize: PAGE_SIZE,
      SortBy: Api.Server.SortBy.MostRecent,
    });

    // getHighlighted({
    //   PageNumber: 1,
    //   PageSize: PAGE_SIZE,
    //   SortBy: Api.Server.SortBy.MostRecent,
    // });

    getPaginatedPlayLists({
      PageNumber: 1,
      PageSize: PAGE_SIZE,
      SortBy: Api.Server.SortBy.MostRecent,
    });
  }, [dispatch]);

  return (
    <div className="cus-scrollbar-w-none mb-auto w-full justify-center overflow-y-auto pb-10">
      <div className="border-b border-[#e8e8e9] dark:border-[#484950]">
        <LibraryProgressList
          contents={startedData.map((d) => {
            return { ...d, isLocked: lockedContentsObj?.[d.id!]?.isLocked };
          })}
          badge={{
            title: translate("my-library.started titles"),
            icon: "/assets/icons/my-library-started.svg",
            style: "bg-[#FCAA49] hover:bg-[#FCAA49]",
          }}
          noContent={{
            icon: "/assets/icons/my-library-no-content-started.svg",
            description: translate("my-library.started no content description"),
            buttonText: translate("my-library.started no content button text"),
            // TODO
            onButtonClick: () => {},
          }}
          countTitle={renderTitlesCountTitle(startedMeta.totalCount || 0)}
          isLoading={startedStatus === "loading"}
          onItemPress={onItemPress}
          onSeeMoreClick={() => {
            setSeeMoreModal({
              isOpen: true,
              type: "Started",
            });
          }}
        />
      </div>
      <div className="border-b border-[#e8e8e9] pt-10 dark:border-[#484950]">
        <LibraryProgressList
          contents={completedData.map((d) => {
            return { ...d, isLocked: lockedContentsObj?.[d.id!]?.isLocked };
          })}
          badge={{
            title: translate("my-library.completed titles"),
            icon: "/assets/icons/my-library-completed.svg",
            style: "bg-[#47CB84] hover:bg-[#47CB84]",
          }}
          noContent={{
            icon: "/assets/icons/my-library-no-content-completed.svg",
            title: translate("my-library.completed no content title"),
            description: translate(
              "my-library.completed no content description"
            ),
          }}
          countTitle={renderTitlesCountTitle(completedMeta.totalCount || 0)}
          isLoading={completedStatus === "loading"}
          onItemPress={onItemPress}
          onSeeMoreClick={() => {
            setSeeMoreModal({
              isOpen: true,
              type: "Completed",
            });
          }}
        />
      </div>
      <div className="border-b border-[#e8e8e9] pt-10 dark:border-[#484950]">
        <LibraryProgressList
          contents={favoriteData.map((d) => {
            return { ...d, isLocked: lockedContentsObj?.[d.id!]?.isLocked };
          })}
          badge={{
            title: translate("my-library.favorite titles"),
            icon: "/assets/icons/my-library-favorite.svg",
            style: "bg-[#F37CA7] hover:bg-[#F37CA7]",
          }}
          noContent={{
            // TODO
            icon: "",
            title: translate("my-library.favorite no content title"),
            description: translate(
              "my-library.favorite no content description"
            ),
          }}
          countTitle={renderTitlesCountTitle(favoriteMeta.totalCount || 0)}
          isLoading={favoriteStatus === "loading"}
          onItemPress={onItemPress}
          onSeeMoreClick={() => {
            setSeeMoreModal({
              isOpen: true,
              type: "Favorite",
            });
          }}
        />
      </div>
      <div className="border-b border-[#e8e8e9] pt-10 dark:border-[#484950]">
        <LibraryProgressList
          contents={bookmarkedData.map((d) => {
            return { ...d, isLocked: lockedContentsObj?.[d.id!]?.isLocked };
          })}
          badge={{
            title: translate("my-library.bookmarked titles"),
            icon: "/assets/icons/my-library-bookmarked.svg",
            style: "bg-[#FD7777] hover:bg-[#FD7777]",
          }}
          noContent={{
            icon: "/assets/icons/my-library-no-content-bookmarked.svg",
            title: translate("my-library.bookmarked no content title"),
            description: translate(
              "my-library.bookmarked no content description"
            ),
          }}
          countTitle={renderBookmarksCountTitle(bookmarkedMeta.totalCount || 0)}
          isLoading={bookmarkedStatus === "loading"}
          onItemPress={({
            id,
            contentEntityTypeId,
            title,
            author,
            image,
            count,
            progress,
            contentEntityType,
          }) => {
            const isLocked = lockedContents?.find((l) => l.id === id)?.isLocked;

            setItemDetailsModal({
              isOpen: true,
              type: "Bookmarked",
              id,
              contentEntityTypeId,
              title,
              author,
              image,
              count,
              progress,
              contentEntityType,
              isLocked,
            });
          }}
          onSeeMoreClick={() => {
            setSeeMoreModal({
              isOpen: true,
              type: "Bookmarked",
            });
          }}
          hidePremiumIcon
        />
      </div>
      {/* <div className="pt-10 border-b border-[#e8e8e9] dark:border-[#484950]">
        <LibraryProgressList
          contents={highlightedData}
          badge={{
            title: translate("my-library.highlighted titles"),
            icon: "/assets/icons/my-library-highlighted.svg",
            style: "bg-[#F5DE6A]",
          }}
          noContent={{
            icon: "/assets/icons/my-library-no-content-highlighted.svg",
            title: translate("my-library.highlighted no content title"),
            description: translate(
              "my-library.highlighted no content description"
            ),
          }}
          countTitle={renderHighlightsCountTitle(
            highlightedMeta.totalCount || 0
          )}
          isLoading={highlightedStatus === "loading"}
          onItemPress={({
            id,
            contentEntityTypeId,
            title,
            author,
            image,
            count,
            progress,
          }) => {
            setItemDetailsModal({
              isOpen: true,
              type: "Highlighted",
              id,
              contentEntityTypeId,
              title,
              author,
              image,
              count,
              progress,
            });
          }}
          onSeeMoreClick={() => {
            setSeeMoreModal({
              isOpen: true,
              type: "Highlighted",
            });
          }}
        />
      </div> */}
      <div className="border-b border-[#e8e8e9] pt-10 sm:pb-20 dark:border-[#484950]">
        <PlaylistsSection
          contents={playlistsData}
          badge={{
            title: translate("my-library.playlists"),
            icon: "/assets/icons/my-library-playlists.svg",
            style: "bg-[#B8A9E0] hover:bg-[#B8A9E0]",
          }}
          noContent={{
            icon: "/assets/icons/my-library-no-content-playlists.svg",
            title: translate("my-library.playlists no content title"),
          }}
          countTitle={renderplaylistCountTitle(playlistsMeta.totalCount || 0)}
          isLoading={playlistsStatus === "loading"}
          onItemPress={(item) => {
            navigate("/playlist", {
              id: item.id,
            });
          }}
          onSeeMoreClick={() => {
            navigate("/playlists");
          }}
          setIsCreatePlaylistDialogOpen={setIsCreatePlaylistDialogOpen}
        />
      </div>
      {itemDetailsModal.isOpen ? (
        <LibraryItemDetailsModal
          isOpen={itemDetailsModal.isOpen}
          onClose={() => {
            setItemDetailsModal((itemDetailsModal) => {
              return {
                ...itemDetailsModal,
                isOpen: false,
              };
            });
          }}
          type={itemDetailsModal.type}
          details={{
            id: itemDetailsModal.id,
            contentEntityTypeId: itemDetailsModal.contentEntityTypeId,
            title: itemDetailsModal.title,
            author: itemDetailsModal.author,
            image: itemDetailsModal.image,
            count: itemDetailsModal.count,
            progress: itemDetailsModal.progress,
            contentEntityType: itemDetailsModal.contentEntityType,
            isLocked: itemDetailsModal.isLocked,
          }}
          onItemDelete={() => {
            if (itemDetailsModal.type === "Bookmarked") {
              getBookmarked({
                PageNumber: 1,
                PageSize: PAGE_SIZE,
                SortBy: Api.Server.SortBy.MostRecent,
              });
            } else {
              getHighlighted({
                PageNumber: 1,
                PageSize: PAGE_SIZE,
                SortBy: Api.Server.SortBy.MostRecent,
              });
            }
          }}
        />
      ) : null}
      {seeMoreModal.isOpen ? (
        <LibraryProgressListSeeMoreModal
          isOpen={seeMoreModal.isOpen}
          onClose={() => {
            setSeeMoreModal((seeMoreModal) => {
              return {
                ...seeMoreModal,
                isOpen: false,
              };
            });
          }}
          type={seeMoreModal.type}
          onItemClick={(item) => {
            if (
              seeMoreModal.type === "Bookmarked" ||
              seeMoreModal.type === "Highlighted"
            ) {
              const {
                id,
                contentEntityTypeId,
                title,
                author,
                image,
                count,
                progress,
                contentEntityType,
                isLocked,
              } = item;

              setItemDetailsModal({
                isOpen: true,
                type: seeMoreModal.type,
                id,
                contentEntityTypeId,
                title,
                author,
                image,
                count,
                progress,
                contentEntityType,
                isLocked,
              });
            } else {
              onItemPress(item);
            }
          }}
        />
      ) : null}

      {isCreatePlaylistDialogOpen ? (
        <CreatePlaylistDialog
          isOpen={isCreatePlaylistDialogOpen}
          setOpen={setIsCreatePlaylistDialogOpen}
        />
      ) : null}
    </div>
  );
};

const renderTitlesCountTitle = (count: number | null) => {
  if (count == null || count === 0)
    return <TranslatedText text={`my-library.${"no titles"}`} />;

  if (count === 1) return <TranslatedText text={`my-library.${"title"}`} />;
  if (count === 2)
    return <TranslatedText text={`my-library.${"two titles"}`} />;
  if (count <= 10)
    return (
      <>
        <div>{count}</div>
        <TranslatedText text={`my-library.${"titles"}`} />
      </>
    );
  if (count > 10)
    return (
      <>
        <div>{count}</div>
        <TranslatedText text={`my-library.${"title"}`} />
      </>
    );

  return "";
};

const renderBookmarksCountTitle = (count: number | null) => {
  if (count == null || count === 0)
    return <TranslatedText text={`my-library.${"no bookmarks"}`} />;

  if (count === 1) return <TranslatedText text={`my-library.${"bookmark"}`} />;
  if (count === 2)
    return <TranslatedText text={`my-library.${"two bookmarks"}`} />;
  if (count <= 10)
    return (
      <>
        <div>{count}</div>
        <TranslatedText text={`my-library.${"bookmarks"}`} />
      </>
    );
  if (count > 10)
    return (
      <>
        <div>{count}</div>
        <TranslatedText text={`my-library.${"bookmark"}`} />
      </>
    );

  return "";
};

const renderHighlightsCountTitle = (count: number | null) => {
  if (count == null || count === 0)
    return <TranslatedText text={`my-library.${"no highlights"}`} />;

  if (count === 1) return <TranslatedText text={`my-library.${"highlight"}`} />;
  if (count === 2)
    return <TranslatedText text={`my-library.${"two highlights"}`} />;
  if (count <= 10)
    return (
      <>
        <div>{count}</div>
        <TranslatedText text={`my-library.${"highlights"}`} />
      </>
    );
  if (count > 10)
    return (
      <>
        <div>{count}</div>
        <TranslatedText text={`my-library.${"highlight"}`} />
      </>
    );

  return "";
};

export const renderplaylistCountTitle = (count: number | null) => {
  if (count == null || count === 0)
    return <TranslatedText text={`my-library.${"no lists"}`} />;

  if (count === 1) return <TranslatedText text={`my-library.${"list"}`} />;
  if (count === 2) return <TranslatedText text={`my-library.${"two lists"}`} />;
  if (count <= 10)
    return (
      <>
        <div>{count}</div>
        <TranslatedText text={`my-library.${"lists"}`} />
      </>
    );
  if (count > 10)
    return (
      <>
        <div>{count}</div>
        <TranslatedText text={`my-library.${"list"}`} />
      </>
    );

  return "";
};
